import { useCallback, useEffect, useState } from "react";

import { AppShell } from "./components/app-shell";
import { ErrorNotice, LoadingView } from "./components/feedback";
import { DEFAULT_PATH } from "./constants";
import { SignInScreen } from "./features/auth/sign-in-screen";
import { PushDetailScreen } from "./features/push/push-detail-screen";
import { PushHistoryScreen } from "./features/push/push-history-screen";
import { PushComposeScreen } from "./features/push/push-screen";
import { TicketDetailScreen } from "./features/support/ticket-detail-screen";
import { TicketListScreen } from "./features/support/ticket-list-screen";
import { AdminApiError, requestAdminApi } from "./lib/api-client";
import { authClient } from "./lib/auth-client";
import type { AdminSession } from "./types";

/**
 * 問い合わせ詳細URLからIDを取得する
 *
 * @param pathname - 現在のURLパス
 * @returns 問い合わせID
 */
const extractTicketId = (pathname: string) => {
  const match = pathname.match(/^\/support\/([^/]+)$/);
  const encodedTicketId = match?.[1];

  if (!encodedTicketId) {
    return null;
  }

  try {
    return decodeURIComponent(encodedTicketId);
  } catch (error: unknown) {
    console.error("Failed to decode support ticket ID", error);
    return null;
  }
};

/**
 * お知らせ配信の編集URLから配信IDを取得する
 *
 * @param pathname - 現在のURLパス
 * @returns 配信ID
 */
const extractPushCampaignId = (pathname: string) => {
  const match = pathname.match(/^\/push\/([^/]+)\/edit$/);
  const encodedCampaignId = match?.[1];

  if (!encodedCampaignId) {
    return null;
  }

  try {
    return decodeURIComponent(encodedCampaignId);
  } catch (error: unknown) {
    console.error("Failed to decode push campaign ID", error);
    return null;
  }
};

/**
 * お知らせ配信の詳細URLから配信IDを取得する
 *
 * @param pathname - 現在のURLパス
 * @returns 配信ID
 */
const extractPushDetailCampaignId = (pathname: string) => {
  const match = pathname.match(/^\/push\/([^/]+)$/);
  const encodedCampaignId = match?.[1];

  if (!encodedCampaignId) {
    return null;
  }

  try {
    return decodeURIComponent(encodedCampaignId);
  } catch (error: unknown) {
    console.error("Failed to decode push campaign detail ID", error);
    return null;
  }
};

/**
 * Codocolle管理画面
 *
 * @returns 管理画面
 */
export const App = () => {
  // 管理者セッションを保持する
  const [session, setSession] = useState<AdminSession | null>(null);

  // セッション確認の完了状態を保持する
  const [isSessionReady, setIsSessionReady] = useState(false);

  // セッション確認エラーを保持する
  const [sessionError, setSessionError] = useState<string | null>(null);

  // 現在の画面パスを保持する
  const [pathname, setPathname] = useState(() => {
    return window.location.pathname === "/" ? DEFAULT_PATH : window.location.pathname;
  });

  // サインアウト処理中かを保持する
  const [isSigningOut, setIsSigningOut] = useState(false);

  // 管理者セッションを確認する
  const loadSession = useCallback(async () => {
    setSessionError(null);

    try {
      const result = await requestAdminApi<AdminSession>("/v1/admin/session");
      setSession(result);
    } catch (error: unknown) {
      if (error instanceof AdminApiError && (error.status === 401 || error.status === 403)) {
        setSession(null);
      } else {
        console.error("Failed to load admin session", error);
        setSessionError("管理画面へ接続できませんでした。時間をおいてもう一度お試しください。");
      }
    } finally {
      setIsSessionReady(true);
    }
  }, []);

  // ブラウザー履歴を更新して画面遷移する
  const handleNavigate = useCallback((nextPath: string) => {
    if (window.location.pathname !== nextPath) {
      window.history.pushState(null, "", nextPath);
    }

    setPathname(nextPath);
    window.scrollTo({ top: 0 });
  }, []);

  // サインアウトして認証画面へ戻す
  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);

    try {
      await authClient.signOut();
      setSession(null);
      handleNavigate("/");
    } catch (error: unknown) {
      console.error("Admin sign out failed", error);
      setSessionError("サインアウトできませんでした。もう一度お試しください。");
    } finally {
      setIsSigningOut(false);
    }
  }, [handleNavigate]);

  // 初回表示時に管理者セッションを確認する
  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // ブラウザーの戻る・進む操作へ追従する
  useEffect(() => {
    const handlePopState = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  if (!isSessionReady) {
    return (
      <main className="bootstrap-page">
        <LoadingView label="管理画面を準備しています" />
      </main>
    );
  }

  if (!session) {
    return (
      <>
        {sessionError ? (
          <div className="session-error">
            <ErrorNotice message={sessionError} onRetry={() => void loadSession()} />
          </div>
        ) : null}
        <SignInScreen />
      </>
    );
  }

  const ticketId = extractTicketId(pathname);
  const pushCampaignId = extractPushCampaignId(pathname);
  const pushDetailCampaignId = extractPushDetailCampaignId(pathname);
  const activeSection = pathname.startsWith("/push") ? "push" : "support";
  const isPushComposePath = pathname === "/push/new" || pushCampaignId !== null;
  const isPushDetailPath = pushDetailCampaignId !== null;
  const content =
    activeSection === "push" ? (
      isPushComposePath ? (
        <PushComposeScreen
          key={pushCampaignId ?? "new"}
          campaignId={pushCampaignId}
          onBack={() => handleNavigate("/push")}
          onPublished={() => handleNavigate("/push")}
        />
      ) : isPushDetailPath ? (
        <PushDetailScreen
          campaignId={pushDetailCampaignId}
          onBack={() => handleNavigate("/push")}
          onEditDraft={(campaignId) =>
            handleNavigate(`/push/${encodeURIComponent(campaignId)}/edit`)
          }
        />
      ) : (
        <PushHistoryScreen
          onCreate={() => handleNavigate("/push/new")}
          onOpenCampaign={(campaignId) => handleNavigate(`/push/${encodeURIComponent(campaignId)}`)}
        />
      )
    ) : ticketId ? (
      <TicketDetailScreen ticketId={ticketId} onBack={() => handleNavigate("/support")} />
    ) : (
      <TicketListScreen
        onOpenTicket={(selectedTicketId) =>
          handleNavigate(`/support/${encodeURIComponent(selectedTicketId)}`)
        }
      />
    );

  return (
    <AppShell
      activeSection={activeSection}
      session={session}
      isSigningOut={isSigningOut}
      onNavigate={handleNavigate}
      onSignOut={() => void handleSignOut()}
    >
      {sessionError ? <ErrorNotice message={sessionError} /> : null}
      {content}
    </AppShell>
  );
};
